import * as XLSX from 'xlsx';
import { Medication } from "./types";
import { ChildPracticeMetadata } from "../../../types";
import { getCorpChildrenMedication } from './services'

const getRefillStatus = (status: string) => {
  let itemStatus;

  switch (status) {
    case '0':
      itemStatus = 'Automatic Refill Off';
      break;
    case '1':
      itemStatus = 'Automatic Refill On';
      break;
    case '2':
      itemStatus = 'Refill On Approval';
      break;
    case '4':
      itemStatus = 'On Membership';
      break;
    default:
      itemStatus = 'N/A';
  }
  return itemStatus;
};

export const exportByType = async (customerNumber: string, type: string) => {
  let queryParams = '?sortBy=customer_number&sortDir=ASC'
  if(type !== 'all') queryParams += `&scope=${type}`

  
  const items: Medication[] = await getCorpChildrenMedication(customerNumber, queryParams)

  if (items.length === 0) {
    throw new Error('There are no medications to export');
  }

  const wb = XLSX.utils.book_new();
  const capitalizeFirstLetter = type.charAt(0).toUpperCase() + type.slice(1);
  const sheetName = `${capitalizeFirstLetter} Medications`;
  const formattedItems = items
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(item => ({
      'Practice Name': item.name,
      'Customer Number': item.customer_number,
      'Doctor Name': item.doctor_name,
      Address: item.address,
      City: item.city,
      State: item.state,
      Zip: item.zip,
      'Kit Number': item.kit_serial_number ? item.kit_serial_number : 'N/A',
      'Kit Model': item.kit_model ? item.kit_model : 'N/A',
      'Item Number': item.item_number,
      'Lot Number': item.lot_number,
      'Refill Enabled': getRefillStatus(String(item.item_refill_status)),
      Item: item.item_description + (item.is_backordered ? ' (on backorder)' : ''),
      Quantity: item.quantity,
      'Expiration Date': item.expiration_date,
      'Date of Purchase': item.purchase_date,
      'Invoice Number': item.invoice_number,
      'Tracking Number': item.tracking_number
    }));
  const ws = XLSX.utils.json_to_sheet(formattedItems);

  XLSX.utils.book_append_sheet(wb, ws, sheetName);

  XLSX.writeFile(wb, `${sheetName}.xlsx`);
}

export const exportResultsByPractice = (childPractices: ChildPracticeMetadata[]) => {
  const customers = structuredClone(childPractices);

  if (customers.length) {
    const wb = XLSX.utils.book_new();
    const sheetName = 'Practice Medications Status';

    const formattedCustomers = customers.sort((a, b) => a.name.localeCompare(b.name))
      .map(customer => ({
        'Practice Name': customer.name,
        'Customer Number': customer.customer_number,
        Location: `${customer.city}, ${customer.state}`,
        Kits: customer.active_med_kits,
        'Expired Items': customer.expired_meds,
        Message: customer.kit_message_short
      }));

    const ws = XLSX.utils.json_to_sheet(formattedCustomers);

    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    XLSX.writeFile(wb, `${sheetName}.xlsx`);
  } else {
    throw new Error('There are no practices to export');
  }
}

export const exportKitsByPractice = async (customerNumber: string, childPractices: ChildPracticeMetadata[]) => {

  const queryParams = '?sortBy=customer_number&sortDir=ASC'
  const items: Medication[] = await getCorpChildrenMedication(customerNumber, queryParams)

  if (items.length === 0) {
    throw new Error('There are no medications to export');
  }

  const wb = XLSX.utils.book_new();
  const customerSet = new Set(childPractices.map(practice => practice.customer_number));
  const sortedItems = items
    .filter(item => customerSet.has(item.customer_number))
    .sort((a, b) => a.name.localeCompare(b.name));

  let ws: XLSX.WorkSheet | undefined;
  let sheetName = '';
  let currentCustomer = '';
  let currentKit = '';

  const generateCustomerHeader = (item: Medication) => [
    { A: item.name },
    { A: item.doctor_name },
    { A: item.address },
    { A: `${item.city}, ${item.state} ${item.zip}` },
    { A: `Customer #: ${item.customer_number}` },
  ];

  const generateKitDetails = (item: Medication) => [
    { A: '' },
    { A: 'Kit Number', B: item.kit_serial_number || 'N/A' },
    { A: 'Kit Model', B: item.kit_model || 'N/A' },
    { A: 'On Refill', B: getRefillStatus(String(item.kit_refill_status)) },
    {
      A: 'Quantity',
      B: 'Item',
      C: 'Lot Number',
      D: 'Item Number',
      E: 'Expiration Date',
      F: 'Refilled',
      G: 'Purchase Date',
      H: 'Invoice Number',
      I: 'Tracking Number',
    },
  ];

  const generateKitItemRow = (item: Medication) => [
    {
      A: item.quantity,
      B: item.item_description + (item.is_backordered ? ' (on backorder)' : ''),
      C: item.lot_number,
      D: item.item_number,
      E: item.expiration_date,
      F: getRefillStatus(String(item.item_refill_status)),
      G: item.purchase_date,
      H: item.invoice_number,
      I: item.tracking_number,
    },
  ];

  for (const item of sortedItems) {
    const isNewCustomer = item.customer_number !== currentCustomer;
    const isNewKit = item.kit_serial_number !== currentKit;

    if (isNewCustomer) {
      if (ws && sheetName) {
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      }

      sheetName = `${item.state.slice(0, 2)}_${item.name.slice(0, 10)}_${item.customer_number}`;
      currentCustomer = item.customer_number;
      currentKit = '';
      const customerHeader = generateCustomerHeader(item);
      ws = XLSX.utils.json_to_sheet(customerHeader, { skipHeader: true });
    }

    if (isNewKit && ws) {
      currentKit = item.kit_serial_number || '';
      const kitDetails = generateKitDetails(item);
      XLSX.utils.sheet_add_json(ws, kitDetails, { skipHeader: true, origin: -1 });
    }

    if (ws) {
      const kitItemRow = generateKitItemRow(item);
      XLSX.utils.sheet_add_json(ws, kitItemRow, { skipHeader: true, origin: -1 });
    }
  }

  if (ws && sheetName) {
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
  }

  XLSX.writeFile(wb, 'medications_compliance_report.xlsx');
}